import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Snackbar, Alert, AlertTitle, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import api from '../api/client';
import { getAccessToken } from '../api/tokenStore';

const NotificationContext = createContext(null);

const VALID_SEVERITY = ['info', 'success', 'warning', 'error'];

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [toast, setToast] = useState(null); // notification shown as a toast
  const esRef = useRef(null);
  // Highest id known at connection time: used to "toast" only genuinely new
  // notifications (not the initial flush of unread ones).
  const seenMaxIdRef = useRef(0);
  const readyRef = useRef(false);

  const unreadCount = notifications.filter(n => !n.read).length;

  const addNotification = useCallback((notif) => {
    setNotifications(prev => {
      if (prev.find(n => n.id === notif.id)) return prev;
      return [notif, ...prev];
    });
  }, []);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      seenMaxIdRef.current = 0;
      readyRef.current = false;
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
      return;
    }

    // Load existing notifications from REST API first
    api.get('/auth/notifications/').then(({ data }) => {
      setNotifications(data);
      seenMaxIdRef.current = data.reduce((m, n) => Math.max(m, n.id), 0);
      readyRef.current = true;
    }).catch(() => { readyRef.current = true; });

    const token = getAccessToken();
    const es = new EventSource(`/api/auth/notifications/stream/?token=${encodeURIComponent(token)}`);
    esRef.current = es;

    es.onmessage = (e) => {
      try {
        const notif = JSON.parse(e.data);
        addNotification(notif);
        // Toast only for a notification created after load (id higher than
        // anything we knew about), not for the initial flush.
        if (readyRef.current && notif.id > seenMaxIdRef.current) {
          setToast(notif);
        }
        seenMaxIdRef.current = Math.max(seenMaxIdRef.current, notif.id);
      } catch {}
    };

    es.onerror = () => {
      // EventSource reconnects automatically — only close if the token is expired
      // (backend returns 401, which triggers onerror with readyState CLOSED)
      if (es.readyState === EventSource.CLOSED) {
        esRef.current = null;
      }
    };

    return () => { es.close(); esRef.current = null; };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const markRead = useCallback(async (id) => {
    await api.post(`/auth/notifications/${id}/read/`);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  }, []);

  const markAllRead = useCallback(async () => {
    await api.post('/auth/notifications/read-all/');
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const handleToastClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setToast(null);
  };

  const handleToastClick = () => {
    if (toast?.id && !toast.read) markRead(toast.id);
    if (toast?.action_url) navigate(toast.action_url);
    setToast(null);
  };

  const severity = VALID_SEVERITY.includes(toast?.type) ? toast.type : 'info';

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, markRead, markAllRead }}>
      {children}
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={6000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={severity}
          variant="filled"
          onClose={handleToastClose}
          sx={{ maxWidth: 380, alignItems: 'flex-start', cursor: toast?.action_url ? 'pointer' : 'default' }}
          action={toast?.action_url ? (
            <Button color="inherit" size="small" onClick={handleToastClick}>Voir</Button>
          ) : null}
        >
          {toast?.title && <AlertTitle sx={{ mb: toast?.body ? 0.5 : 0 }}>{toast.title}</AlertTitle>}
          {toast?.body}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => useContext(NotificationContext);
